import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiPost, apiGet } from '../utils/apiAuth';
import config from '../config';
import CustomModal from '../Component/Modals/CustomModal';

const popupStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1050,
    backdropFilter: 'blur(2px)',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
  },
  header: {
    padding: '20px 24px 16px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f8f5ff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#4c1d95',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 24px',
  },
  commentItem: {
    padding: '12px 16px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)',
    borderLeft: '3px solid #8b5cf6',
    color: '#4c1d95',
  },
  commentText: {
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '8px',
    wordBreak: 'break-word',
  },
  commentMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    color: '#6b21a8',
  },
  timeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  time: {
    fontSize: '12px',
    fontWeight: 500,
  },
  date: {
    fontSize: '11px',
    color: '#a855f7',
  },
  footer: {
    padding: '16px 24px 20px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
  },
  textarea: {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'vertical',
    minHeight: '40px',
  },
  submitButton: {
    borderRadius: '8px',
    minWidth: '120px',
  },
  error: {
    textAlign: 'center',
    color: '#dc2626',
  },
  empty: {
    textAlign: 'center',
    color: '#6b7280',
    fontStyle: 'italic',
  },
};

const ClonePopup = ({ 
  isOpen, 
  onClose, 
  onRefresh,
  referenceType, 
  referenceId, 
  userEmail, 
  userName 
}) => {
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const commentsEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new comments are added
  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset form when popup opens
  useEffect(() => {
    if (isOpen) {
      setCommentText('');
      setError('');
    }
  }, [isOpen]);

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiGet(
        `${config.API_BASE_URL}/api/comments/?reference_type=${referenceType}&reference_id=${referenceId}&comment_type=clone`
      );
      
      if (response.status === 200) {
        setComments(response.data.comments || []);
      } else {
        setError('Failed to load clone comments');
      }
    } catch (error) {
      setError('Failed to load clone comments');
    } finally {
      setLoading(false);
    }
  }, [referenceType, referenceId]);

  // Load comments when popup opens
  useEffect(() => {
    if (isOpen && referenceType && referenceId && referenceId !== 'null' && referenceId !== 'undefined') {
      loadComments();
    }
  }, [isOpen, referenceType, referenceId, loadComments]);

  // Scroll to bottom when comments change
  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const handleSubmitClone = async () => {
    if (!commentText.trim()) return;
    
    // Validate required fields
    if (!referenceType || !referenceId || referenceId === 'null' || referenceId === 'undefined' || referenceId === null || referenceId === undefined) {
      setError('Invalid reference data. Please try again.');
      return;
    }
    
    if (!userEmail || !userName) {
      setError('User information not available. Please refresh the page.');
      return;
    }
    
    setShowConfirmModal(true);
  };

  const confirmCloneSubmission = async () => {
    try {
      setSubmitting(true);
      setError('');
      setShowConfirmModal(false);
      
      const payload = {
        reference_type: referenceType,
        reference_id: referenceId,
        comment_text: commentText.trim(),
        cloned_by: userEmail,
        cloned_by_name: userName
      };
      
      const response = await apiPost(`${config.API_BASE_URL}/api/comments/clone`, payload);
      
      if (response.status === 200 || response.status === 201) {
        // Reload comments to show the new clone comment
        await loadComments();
        setShowSuccessModal(true);
      } else {
        setError('Failed to clone content');
      }
    } catch (error) {
      setError('Failed to clone content');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    onClose();
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitClone();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' };
    const dateOptions = { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' };
    const timeStr = istDate.toLocaleTimeString('en-IN', timeOptions);
    const dateStr = istDate.toLocaleDateString('en-IN', dateOptions);
    return (
      <div style={popupStyles.timeContainer}>
        <div style={popupStyles.time}>{timeStr}</div>
        <div style={popupStyles.date}>{dateStr}</div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div style={popupStyles.overlay} onClick={onClose}>
      <div style={popupStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={popupStyles.header}>
          <h3 style={popupStyles.title}>Clone Content</h3>
          <button style={popupStyles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={popupStyles.body}>
          {loading ? (
            <div className="d-flex flex-column align-items-center text-muted py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading clone comments...</span>
              </div>
              <span className="mt-2">Loading clone comments...</span>
            </div>
          ) : error ? (
            <div className="text-center">
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
              <button onClick={loadComments} className="btn btn-primary btn-sm">
                Retry
              </button>
            </div>
          ) : comments.length === 0 ? (
            <div style={popupStyles.empty}>
              No clone comments yet. Add a comment when cloning!
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {comments.map((comment, index) => (
                <div key={comment.id || index} style={popupStyles.commentItem}>
                  <div style={popupStyles.commentText}>{comment.comment_text}</div>
                  <div style={popupStyles.commentMeta}>
                    <span>{comment.commented_by_name}</span>
                    {formatTime(comment.commented_at)}
                  </div>
                </div>
              ))}
              <div ref={commentsEndRef} />
            </div>
          )}
        </div>

        <div style={popupStyles.footer}>
          <div className="d-flex flex-column flex-md-row gap-3">
            <textarea
              ref={inputRef}
              style={popupStyles.textarea}
              className="form-control"
              placeholder="Add a comment for this clone..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={2}
              disabled={submitting}
            />
            <button
              type="button"
              className="btn btn-primary"
              style={popupStyles.submitButton}
              onClick={handleSubmitClone}
              disabled={!commentText.trim() || submitting}
            >
              {submitting ? 'Cloning...' : 'Clone Content'}
            </button>
          </div>
        </div>
      </div>

      <CustomModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        title="Confirm Clone"
        size="md"
        centered
        showFooter={false}
        content={
          <div>
            <p className="mb-3">Are you sure you want to clone this content?</p>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmCloneSubmission}
                disabled={submitting}
              >
                {submitting ? 'Cloning...' : 'Confirm Clone'}
              </button>
            </div>
          </div>
        }
      />

      <CustomModal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        title="Clone Successful"
        size="md"
        centered
        footerText="OK"
        content={<p className="mb-0">Content cloned successfully!</p>}
      />
    </div>
  );
};

export default ClonePopup;

